import type { MarketVariants, SiteVariants } from '@/lib/i18n'

export type Slide04Copy = {
  title: string
  /** Body paragraph. Must contain the sentinel "{trigger}" exactly once. It's
   *  replaced inline by the popover-button rendering the `triggerText` word. */
  body: string
  triggerText: string

  /** The clumsy 1990s-style email mockup. */
  oldEmailWindowTitle: string
  oldEmailGreeting: string
  oldEmailBody: string
  oldEmailSignoff: string
  oldEmailSignature: string
  oldCaption: string

  smsSender: string
  /** Single letter shown in the sender avatar circle. */
  smsAvatarInitial: string
  smsBody: string
  /** The phrase inside `smsBody` that renders as the fake tap-to-verify link.
   *  Matched case-insensitively; must appear verbatim in `smsBody`. */
  smsLinkText: string
  /** Tooltip shown over the fake link. */
  smsLinkTooltip: string
  newCaption: string

  popoverTitle: string
  popoverBody: string
  /** Label that precedes the citation, including its trailing space. */
  popoverSourcePrefix: string
  popoverSource: string
}

export const copy: MarketVariants<Slide04Copy> = {
  US: {
    title: 'Scammers have gone pro',
    body: "With the help of AI, today's scams are personalized, emotionally intelligent, and professionally designed. The old {trigger} are gone.",
    triggerText: 'tells',

    oldEmailWindowTitle: 'New Message',
    oldEmailGreeting: 'Greetings,',
    oldEmailBody:
      'I am Prince Wamba of Nigeria. I need help transferrring 20 million dollers. Please sned me your bank details and you will be reichly rewarded.',
    oldEmailSignoff: 'Please respond,',
    oldEmailSignature: 'Prince Wamba',
    oldCaption: 'Spelling mistakes. Poor grammar. Generic.',

    smsSender: 'Bank Fraud Alert',
    smsAvatarInitial: 'B',
    smsBody:
      'Did you make a $326 purchase at Costco? To prevent card suspension, verify now.',
    smsLinkText: 'verify now',
    smsLinkTooltip: 'Never tap a link in a message. Type the web address yourself instead.',
    newCaption: 'Specific. Professional. Urgent.',

    popoverTitle: 'The spelling-mistake myth',
    popoverBody:
      'Despite over 73% of people feeling confident they can spot a scam, the most common method they reported was checking for spelling mistakes and poor grammar. With the help of AI, scammers have virtually eliminated these errors. That old advice is now outdated, and relying on it is risky.',
    popoverSourcePrefix: 'Source: ',
    popoverSource: 'Nationwide Building Society, 2024',
  },
  GB: {
    smsBody:
      'Did you make a £326 purchase at Costco? To prevent card suspension, verify now.',
  },
  // CA and AU inherit the US smsBody ($326), no override needed.
  QA: {
    // Riyals and a retailer with branches across Doha. Everything else on the
    // slide is market-neutral.
    smsBody:
      'Did you make a QAR 1,190 purchase at Carrefour? To prevent card suspension, verify now.',
  },
  ar: {
    title: 'المحتالون أصبحوا محترفين',
    body: 'بمساعدة الذكاء الاصطناعي، أصبحت عمليات الاحتيال اليوم مخصّصة لكل شخص، وذكية عاطفياً، ومصمّمة باحترافية. أما {trigger} القديمة فقد اختفت.',
    triggerText: 'العلامات الفاضحة',

    oldEmailWindowTitle: 'رسالة جديدة',
    oldEmailGreeting: 'تحياتي،',
    // Deliberately clumsy, the way machine-translated spam reads in Arabic:
    // مساعده for مساعدة, إرسل for أرسل, and a stilted passive at the end.
    oldEmailBody:
      'أنا الأمير وامبا من نيجيريا. أنا بحاجة إلى مساعده لتحويل 20 مليون دولار. من فضلك إرسل لي تفاصيل البنك الخاص بك وسوف يتم مكافأتك بسخاء كبير.',
    oldEmailSignoff: 'أرجو الرد،',
    oldEmailSignature: 'الأمير وامبا',
    oldCaption: 'أخطاء إملائية. لغة ركيكة. رسالة عامة.',

    smsSender: 'تنبيه احتيال من البنك',
    smsAvatarInitial: 'ب',
    smsBody: 'هل قمت بعملية شراء بقيمة 1,190 ر.ق من كارفور؟ لتجنّب إيقاف بطاقتك، تحقّق الآن.',
    smsLinkText: 'تحقّق الآن',
    smsLinkTooltip: 'لا تضغط أبداً على رابط في رسالة. اكتب عنوان الموقع بنفسك بدلاً من ذلك.',
    newCaption: 'محدّدة. احترافية. مستعجلة.',

    popoverTitle: 'خرافة الأخطاء الإملائية',
    popoverBody:
      'رغم أن أكثر من 73% من الناس يشعرون بالثقة في قدرتهم على اكتشاف الاحتيال، فإن الطريقة الأكثر شيوعاً التي ذكروها هي البحث عن الأخطاء الإملائية واللغة الركيكة. وبمساعدة الذكاء الاصطناعي، تخلّص المحتالون من هذه الأخطاء تقريباً. تلك النصيحة القديمة لم تعد صالحة، والاعتماد عليها أمر محفوف بالمخاطر.',
    popoverSourcePrefix: 'المصدر: ',
    popoverSource: 'Nationwide Building Society، 2024',
  },
}

// DCUC (military) tenant: swap the retailer for the on-base Exchange so the
// example lands with service members. Other tenants keep the Costco copy.
const militaryAi: Partial<Slide04Copy> = {
  smsBody:
    'Did you make a $326 purchase at the Exchange? To prevent card suspension, verify now.',
}

export const siteCopy: SiteVariants<Slide04Copy> = {
  dcuc: militaryAi,
  amba: militaryAi,
}
